import React, { useMemo, useState } from "react";
import { Button, Modal, Tabs, Tag, Typography, Radio, message } from "antd";
import {
  CheckCircleOutlined,
  CloseCircleOutlined,
  InfoCircleOutlined,
} from "@ant-design/icons";
import { bulkImportTasks, bulkImportTasksJson } from "services/pmService";

const { Text, Paragraph } = Typography;

// Example JSON format for users
const EXAMPLE_JSON = [
  {
    rowNumber: 1,
    title: "Task title",
    description: "Task description",
    assigneeEmail: "user@example.com",
    priority: "1",
    points: "3",
    dueDate: "2026-02-20",
    createdByName: "John Doe",
    startedAt: "2026-02-01",
    actualCompletionDate: "",
    prDoc: "",
    comment: "",
    acceptanceCriteria: ""
  }
];

const BulkImportModal = ({
  open,
  onClose,
  projectId,
  iterationId,
  onImported,
  actorId,
}) => {
  const [file, setFile] = useState(null);
  const [jsonInput, setJsonInput] = useState("");
  const [importType, setImportType] = useState("file"); // 'file' or 'json'
  const [uploading, setUploading] = useState(false);
  const [result, setResult] = useState(null);
  const [errors, setErrors] = useState([]);

  const canUpload = useMemo(() => {
    if (importType === "file") {
      return Boolean(file) && !uploading;
    }
    return jsonInput.trim().length > 0 && !uploading;
  }, [file, jsonInput, importType, uploading]);

  const resetState = () => {
    setFile(null);
    setJsonInput("");
    setErrors([]);
    setResult(null);
    setUploading(false);
    setImportType("file");
  };

  const handleClose = () => {
    resetState();
    onClose?.();
  };

  const handleFileChange = (event) => {
    const selectedFile = event.target.files?.[0] || null;
    setErrors([]);
    setResult(null);
    setFile(selectedFile);
  };

  const handleUpload = async () => {
    if (!projectId || !iterationId) return;

    try {
      setUploading(true);
      setErrors([]);

      let response;
      if (importType === "file") {
        if (!file) {
          setErrors([{ row: 0, message: "Please select a file" }]);
          setUploading(false);
          return;
        }
        response = await bulkImportTasks(projectId, iterationId, file, actorId);
      } else {
        // JSON import
        try {
          const tasks = JSON.parse(jsonInput);
          if (!Array.isArray(tasks)) {
            throw new Error("JSON must be an array of tasks");
          }
          response = await bulkImportTasksJson(projectId, iterationId, tasks, actorId);
        } catch (parseError) {
          setErrors([{ row: 0, message: "Invalid JSON: " + parseError.message }]);
          setUploading(false);
          return;
        }
      }

      setResult(response);
      const rowErrors = Array.isArray(response?.errors) ? response.errors : [];
      setErrors(rowErrors);
      onImported?.(response);

      if ((response?.failed || 0) === 0) {
        // Don't auto-close so user can see results
      }
    } catch (error) {
      const responseData = error?.response?.data;
      const status = error?.response?.status;
      const messageText =
        responseData?.message ||
        responseData?.error ||
        (typeof responseData === "string" ? responseData : null) ||
        (status ? `Request failed with status ${status}` : null) ||
        error?.message ||
        "Failed to import tasks";
      setErrors([{ row: 0, message: messageText }]);
    } finally {
      setUploading(false);
    }
  };

  const tabItems = [];

  if (result) {
    // Summary tab
    tabItems.push({
      key: "summary",
      label: "Summary",
      children: (
        <div style={{ padding: "8px 0" }}>
          <div
            style={{
              display: "grid",
              gridTemplateColumns: "1fr 1fr 1fr",
              gap: 12,
              marginBottom: 16,
            }}
          >
            <div
              style={{
                textAlign: "center",
                padding: 12,
                background: "#f0f5ff",
                borderRadius: 8,
              }}
            >
              <div style={{ fontSize: 24, fontWeight: 700 }}>
                {result.total}
              </div>
              <Text type="secondary">Total</Text>
            </div>
            <div
              style={{
                textAlign: "center",
                padding: 12,
                background: "#f6ffed",
                borderRadius: 8,
              }}
            >
              <div
                style={{ fontSize: 24, fontWeight: 700, color: "#52c41a" }}
              >
                {result.success}
              </div>
              <Text type="secondary">Success</Text>
            </div>
            <div
              style={{
                textAlign: "center",
                padding: 12,
                background: result.failed > 0 ? "#fff2f0" : "#f6f6f6",
                borderRadius: 8,
              }}
            >
              <div
                style={{
                  fontSize: 24,
                  fontWeight: 700,
                  color: result.failed > 0 ? "#ff4d4f" : "#999",
                }}
              >
                {result.failed}
              </div>
              <Text type="secondary">Failed</Text>
            </div>
          </div>
        </div>
      ),
    });

    // Created tasks tab
    if (result.created?.length > 0) {
      tabItems.push({
        key: "created",
        label: (
          <span>
            <CheckCircleOutlined style={{ color: "#52c41a" }} /> Created (
            {result.created.length})
          </span>
        ),
        children: (
          <div style={{ maxHeight: 300, overflowY: "auto" }}>
            {result.created.map((item, idx) => (
              <div
                key={idx}
                style={{
                  padding: "6px 0",
                  borderBottom: "1px solid #f0f0f0",
                  display: "flex",
                  justifyContent: "space-between",
                }}
              >
                <div>
                  <Tag color="green">Row {item.row}</Tag>
                  <Text>{item.title}</Text>
                </div>
                <Text type="secondary" copyable style={{ fontSize: 11 }}>
                  {item.taskId}
                </Text>
              </div>
            ))}
          </div>
        ),
      });
    }

    // Errors tab
    if (errors.length > 0) {
      tabItems.push({
        key: "errors",
        label: (
          <span>
            <CloseCircleOutlined style={{ color: "#ff4d4f" }} /> Errors (
            {errors.length})
          </span>
        ),
        children: (
          <div style={{ maxHeight: 300, overflowY: "auto" }}>
            {errors.map((error, index) => (
              <div
                key={`${error.row}-${index}`}
                style={{
                  padding: "6px 0",
                  borderBottom: "1px solid #f0f0f0",
                }}
              >
                {error.row > 0 && <Tag color="red">Row {error.row}</Tag>}
                <Text type="danger">{error.message}</Text>
              </div>
            ))}
          </div>
        ),
      });
    }

    // Captured data tab
    if (result.captured?.length > 0) {
      tabItems.push({
        key: "captured",
        label: (
          <span>
            <InfoCircleOutlined /> Captured ({result.captured.length})
          </span>
        ),
        children: (
          <div style={{ maxHeight: 300, overflowY: "auto" }}>
            {result.captured.map((item, idx) => (
              <div
                key={idx}
                style={{
                  padding: "8px 0",
                  borderBottom: "1px solid #f0f0f0",
                }}
              >
                <div
                  style={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                  }}
                >
                  <div>
                    <Tag color="blue">Row {item.row}</Tag>
                    <Text strong>{item.title}</Text>
                  </div>
                  {item.saved ? (
                    <Tag color="green">Saved</Tag>
                  ) : (
                    <Tag color="orange">Not Saved</Tag>
                  )}
                </div>
                {item.message && (
                  <Text
                    type="secondary"
                    style={{ fontSize: 12, display: "block", marginTop: 4 }}
                  >
                    {item.message}
                  </Text>
                )}
                <div
                  style={{
                    fontSize: 11,
                    color: "#999",
                    marginTop: 4,
                    display: "flex",
                    flexWrap: "wrap",
                    gap: 8,
                  }}
                >
                  {item.assignee && <span>Assignee: {item.assignee}</span>}
                  {item.points && <span>Points: {item.points}</span>}
                  {item.priority && <span>Priority: {item.priority}</span>}
                </div>
              </div>
            ))}
          </div>
        ),
      });
    }
  }

  return (
    <Modal
      title="Import Tasks"
      open={open}
      onCancel={handleClose}
      centered
      getContainer={document.body}
      maskClosable
      keyboard
      destroyOnClose
      width={result ? 600 : 460}
      zIndex={4000}
      styles={{
        mask: {
          backgroundColor: "rgba(10, 15, 20, 0.72)",
          backdropFilter: "blur(3px)",
        },
      }}
      footer={[
        <Button key="cancel" onClick={handleClose} disabled={uploading}>
          {result ? "Close" : "Cancel"}
        </Button>,
        !result && (
          <Button
            key="upload"
            type="primary"
            onClick={handleUpload}
            loading={uploading}
            disabled={!canUpload}
          >
            Import
          </Button>
        ),
        result && (
          <Button
            key="new"
            type="primary"
            onClick={resetState}
          >
            Import Another
          </Button>
        ),
      ].filter(Boolean)}
    >
      {!result ? (
        <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
          {/* Import Type Selector */}
          <div>
            <Text strong style={{ display: "block", marginBottom: 8 }}>
              Import Method:
            </Text>
            <Radio.Group
              value={importType}
              onChange={(e) => {
                setImportType(e.target.value);
                setErrors([]);
                setFile(null);
                setJsonInput("");
              }}
            >
              <Radio value="file">Upload .docx File</Radio>
              <Radio value="json">Paste JSON</Radio>
            </Radio.Group>
          </div>

          {importType === "file" ? (
            <div>
              <Text type="secondary" style={{ display: "block", marginBottom: 8 }}>
                Upload a .docx file to bulk import tasks into this iteration.
              </Text>
              <input
                type="file"
                accept=".docx"
                onChange={handleFileChange}
                disabled={uploading}
              />
            </div>
          ) : (
            <div>
              <Text type="secondary" style={{ display: "block", marginBottom: 8 }}>
                Paste JSON array of tasks to import. Only actualCompletionDate, prDoc, comment, and acceptanceCriteria are optional.
              </Text>
              <textarea
                value={jsonInput}
                onChange={(e) => {
                  setJsonInput(e.target.value);
                  setErrors([]);
                }}
                placeholder={`[\n  {\n    "title": "Task title",\n    "description": "Description",\n    "assigneeEmail": "user@example.com",\n    "priority": "1",\n    "points": "3",\n    "dueDate": "2026-02-20"\n  }\n]`}
                disabled={uploading}
                style={{
                  width: "100%",
                  height: 200,
                  fontFamily: "monospace",
                  fontSize: 12,
                  padding: 8,
                  borderRadius: 4,
                  border: "1px solid #d9d9d9"
                }}
              />
              <Button
                type="link"
                size="small"
                onClick={() => setJsonInput(JSON.stringify(EXAMPLE_JSON, null, 2))}
                style={{ padding: 0, marginTop: 4 }}
              >
                Load
              </Button>
            </div>
          )}

          {errors.length > 0 && !result && (
            <div style={{ maxHeight: 220, overflowY: "auto" }}>
              <strong style={{ color: "#ff4d4f" }}>Import errors</strong>
              <ul style={{ marginTop: 8, paddingLeft: 16 }}>
                {errors.map((error, index) => (
                  <li key={`${error.row}-${index}`} style={{ color: "#ff4d4f" }}>
                    {error.row > 0 ? `Row ${error.row}: ` : ""}
                    {error.message}
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>
      ) : (
        <Tabs items={tabItems} defaultActiveKey="summary" size="small" />
      )}
    </Modal>
  );
};

export default BulkImportModal;
